import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/App";
import { Menu, X, User, LogOut, Download, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Store the install prompt globally
let deferredPrompt = null;

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [canInstall, setCanInstall] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();

  // Listen for PWA install prompt
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      deferredPrompt = e;
      setCanInstall(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setCanInstall(false);
    }
    
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallApp = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setCanInstall(false);
      }
      deferredPrompt = null;
    } else {
      // Fallback for iOS
      alert('To install:\n\niPhone/iPad: Tap Share → "Add to Home Screen"\n\nAndroid: Tap menu (⋮) → "Install App"');
    }
  };

  const navLinks = [
    { path: "/", label: "Home", icon: Home },
    { path: "/support", label: "Support Us", highlight: true },
    { path: "/booking", label: "Booking" },
    { path: "/private-events", label: "Private Events" },
    { path: "/contact", label: "Contact" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/5">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Logo size="md" linkToHome={true} />

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                className={`text-sm font-body tracking-wide transition-colors duration-300 ${
                  link.highlight 
                    ? "text-rose-400 hover:text-rose-300 flex items-center gap-1"
                    : isActive(link.path)
                      ? "text-electric"
                      : "text-white/70 hover:text-white"
                }`}
              >
                {link.highlight && <span className="text-rose-400">♥</span>}
                {link.label}
              </Link>
            ))}
          </div>

          {/* Auth Buttons */}
          <div className="hidden md:flex items-center gap-4">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="flex items-center gap-2 text-white/80 hover:text-white hover:bg-white/5"
                    data-testid="user-menu-trigger"
                  >
                    <User className="w-4 h-4" />
                    <span className="text-sm">{user.name}</span>
                    {user.is_subscriber && (
                      <span className="text-xs bg-electric/20 text-electric px-2 py-0.5 rounded-full">
                        {user.subscription_tier?.toUpperCase() || "MEMBER"}
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-obsidian-light border-white/10">
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard" className="cursor-pointer" data-testid="nav-dashboard">
                      Dashboard
                    </Link>
                  </DropdownMenuItem>
                  {["admin", "manager", "staff"].includes(user.role) && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin" className="cursor-pointer text-electric" data-testid="nav-admin">
                        Admin Panel
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem 
                    onClick={logout}
                    className="cursor-pointer text-red-400"
                    data-testid="nav-logout"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Button 
                  variant="ghost" 
                  onClick={handleInstallApp}
                  className="text-electric hover:text-electric-light hover:bg-white/5 gap-2"
                  data-testid="nav-download-app"
                >
                  <Download className="w-4 h-4" />
                  Get App
                </Button>
                <Link to="/login">
                  <Button 
                    variant="ghost" 
                    className="text-white/70 hover:text-white hover:bg-white/5"
                    data-testid="nav-login"
                  >
                    Sign In
                  </Button>
                </Link>
                <Link to="/register">
                  <Button 
                    className="bg-electric text-obsidian hover:bg-electric-light font-medium"
                    data-testid="nav-register"
                  >
                    Join the Club
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden text-white/80 hover:text-white p-2"
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden py-4 border-t border-white/10">
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className={`px-4 py-2 rounded text-sm ${
                    isActive(link.path)
                      ? "text-electric bg-white/5"
                      : "text-white/70 hover:text-white hover:bg-white/5"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="border-t border-white/10 mt-2 pt-2">
                {user ? (
                  <>
                    <Link
                      to="/dashboard"
                      onClick={() => setIsOpen(false)}
                      className="block px-4 py-2 text-sm text-white/70 hover:text-white"
                    >
                      Dashboard
                    </Link>
                    {["admin", "manager", "staff"].includes(user.role) && (
                      <Link
                        to="/admin"
                        onClick={() => setIsOpen(false)}
                        className="block px-4 py-2 text-sm text-electric font-medium hover:bg-white/5"
                        data-testid="mobile-nav-admin"
                      >
                        ⚙️ Admin Panel
                      </Link>
                    )}
                    <button
                      onClick={() => {
                        logout();
                        setIsOpen(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-white/5"
                    >
                      Logout
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => {
                        handleInstallApp();
                        setIsOpen(false);
                      }}
                      className="flex items-center gap-2 w-full px-4 py-2 text-sm text-electric hover:bg-white/5"
                    >
                      <Download className="w-4 h-4" />
                      Get App
                    </button>
                    <Link
                      to="/login"
                      onClick={() => setIsOpen(false)}
                      className="block px-4 py-2 text-sm text-white/70 hover:text-white"
                    >
                      Sign In
                    </Link>
                    <Link
                      to="/register"
                      onClick={() => setIsOpen(false)}
                      className="block px-4 py-2 text-sm text-electric"
                    >
                      Join the Club
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Navbar;
